import { useRef, useCallback, useEffect } from "react";
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, Copy, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface ContentTabProps {
  content?: string;
}

function cleanMarkdown(md: string): string {
  const lines = md.split("\n");

  // Trim leading nav-like lines: short lines dominated by markdown links
  let startIdx = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) { startIdx = i + 1; continue; }
    // If line is a heading or a long paragraph, it's real content
    if (/^#{1,3}\s/.test(line) || (line.length > 50 && !/^\[/.test(line))) break;
    // Lines that are mostly links (nav menus)
    const linkCount = (line.match(/\[([^\]]*)\]\([^)]*\)/g) || []).length;
    const textWithoutLinks = line.replace(/\[([^\]]*)\]\([^)]*\)/g, "").trim();
    if (linkCount >= 2 && textWithoutLinks.length < 20) {
      startIdx = i + 1;
      continue;
    }
    // Short standalone text that looks like a nav item
    if (line.length < 30 && linkCount >= 1) {
      startIdx = i + 1;
      continue;
    }
    break;
  }

  // Trim trailing footer-like lines
  let endIdx = lines.length;
  for (let i = lines.length - 1; i >= startIdx; i--) {
    const line = lines[i].trim();
    if (!line) { endIdx = i; continue; }
    if (/©|all rights reserved|privacy policy|terms of service|cookie/i.test(line)) {
      endIdx = i;
      continue;
    }
    // Footer link clusters
    const linkCount = (line.match(/\[([^\]]*)\]\([^)]*\)/g) || []).length;
    const textWithoutLinks = line.replace(/\[([^\]]*)\]\([^)]*\)/g, "").trim();
    if (linkCount >= 2 && textWithoutLinks.length < 20) {
      endIdx = i;
      continue;
    }
    break;
  }

  return lines.slice(startIdx, endIdx).join("\n").trim();
}

function markdownToHtml(md: string): string {
  const lines = md.split("\n");
  const html: string[] = [];
  let inUl = false;
  let inOl = false;

  const closeLists = () => {
    if (inUl) { html.push("</ul>"); inUl = false; }
    if (inOl) { html.push("</ol>"); inOl = false; }
  };

  for (const raw of lines) {
    const line = raw;

    // Blank line
    if (!line.trim()) {
      closeLists();
      continue;
    }

    // Headings
    const h3 = line.match(/^### (.+)$/);
    if (h3) { closeLists(); html.push(`<h3 class="text-base font-semibold mt-6 mb-2 text-foreground">${inline(h3[1])}</h3>`); continue; }
    const h2 = line.match(/^## (.+)$/);
    if (h2) { closeLists(); html.push(`<h2 class="text-lg font-semibold mt-8 mb-3 text-foreground">${inline(h2[1])}</h2>`); continue; }
    const h1 = line.match(/^# (.+)$/);
    if (h1) { closeLists(); html.push(`<h1 class="text-2xl font-bold mt-8 mb-4 text-foreground">${inline(h1[1])}</h1>`); continue; }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) { closeLists(); html.push('<hr class="my-6 border-border" />'); continue; }

    // Blockquote
    const bq = line.match(/^>\s?(.*)$/);
    if (bq) { closeLists(); html.push(`<blockquote class="border-l-4 border-primary/40 pl-4 my-3 italic text-muted-foreground">${inline(bq[1])}</blockquote>`); continue; }

    // Unordered list
    const ul = line.match(/^\s*[-*]\s+(.+)$/);
    if (ul) {
      if (inOl) { html.push("</ol>"); inOl = false; }
      if (!inUl) { html.push('<ul class="list-disc pl-6 my-3 space-y-1">'); inUl = true; }
      html.push(`<li class="text-muted-foreground">${inline(ul[1])}</li>`);
      continue;
    }

    // Ordered list
    const ol = line.match(/^\s*\d+\.\s+(.+)$/);
    if (ol) {
      if (inUl) { html.push("</ul>"); inUl = false; }
      if (!inOl) { html.push('<ol class="list-decimal pl-6 my-3 space-y-1">'); inOl = true; }
      html.push(`<li class="text-muted-foreground">${inline(ol[1])}</li>`);
      continue;
    }

    // Paragraph
    closeLists();
    html.push(`<p class="my-2 text-muted-foreground leading-relaxed">${inline(line)}</p>`);
  }

  closeLists();
  return html.join("\n");
}

/** Inline formatting: bold, italic, links, images */
function inline(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "") // strip images
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-primary underline">$1</a>');
}

const ToolbarButton = ({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
  >
    {children}
  </button>
);

export const ContentTab = ({ content }: ContentTabProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (editorRef.current && content) {
      const cleaned = cleanMarkdown(content);
      editorRef.current.innerHTML = markdownToHtml(cleaned);
    }
  }, [content]);

  const exec = useCallback((cmd: string, value?: string) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
  }, []);

  const handleCopy = useCallback(async () => {
    const text = editorRef.current?.innerText || "";
    await navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard" });
  }, [toast]);

  const handleDownload = useCallback(() => {
    const text = editorRef.current?.innerText || "";
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "page-content.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  }, []);

  if (!content) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <FileText className="w-12 h-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-1">No content available</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Page content is only available for new analyses. Re-analyze the URL to generate editable content.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 rounded-lg border border-border bg-card flex-wrap">
        <ToolbarButton onClick={() => exec("bold")} title="Bold">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("italic")} title="Italic">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton onClick={() => exec("formatBlock", "h1")} title="Heading 1">
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("formatBlock", "h2")} title="Heading 2">
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton onClick={() => exec("insertUnorderedList")} title="Bullet List">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("insertOrderedList")} title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={handleCopy}>
          <Copy className="w-3.5 h-3.5" /> Copy
        </Button>
        <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={handleDownload}>
          <Download className="w-3.5 h-3.5" /> Download
        </Button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="min-h-[400px] p-6 rounded-lg border border-border bg-card prose prose-invert prose-sm max-w-none focus:outline-none focus:ring-1 focus:ring-primary/30"
      />
    </div>
  );
};
